import { Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { BaseComponent, projectGeographyKey, stampGridGeography } from "./base_component"
import { SelectControl } from "../controls/select"
import { PreviewControl } from "../controls/preview"
import { NumericTileGrid } from "../tile_grid"
import { numericDataSocket } from "../socket_types"
import { createXYZ } from "ol/tilegrid"
import { TypedArray } from "d3"
import { readRastersForTileRange, retrieveModelDataWCS } from "../model_retrieval"
import { Extent } from "ol/extent"
import { maskFromExtentAndShape } from "../bounding_box"
import { ProjectProperties } from "."
import { ProjectPermissions } from "../../editor/project_editor"

interface DigitalModel {
    id: number
    name: string
    source: string
    /** Socket-style type badge shown in the model picker (Elevation, Canopy, …). */
    badge: string
    toolTip?: string
    toolTipURL?: string
    externalLink?: string
    permission?: string
    type?: string
    region?: string
    unit?: string
}

//TODO: hardcoded scale factors, find an effective way of retrieving them from the geoserver?
const ModelList: Array<DigitalModel> = [
    {
        id: 0,
        name: 'Digital Surface Model',
        source: 'lidar:DSM_2m',
        badge: 'Elevation',
        permission: 'KewLidar',
        type: "Normal",
        region: "UK"
    },
    {
        id: 1,
        name: 'Digital Terrian Model',
        source: 'lidar:DTM_5m',
        badge: 'Elevation',
        permission: 'KewLidar',
        type: "Normal",
        region: "UK"
    },
    {
        id: 2,
        name: 'Feature Height',
        source: 'lidar:Depth',
        badge: 'Canopy',
        permission: 'KewLidar',
        type: "Normal",
        region: "UK"
    },
    {
        id: 3,
        name: 'Canopy Height (Sentinel-2, Equal-Earth)',
        source: 'eth:eth_g_h',
        badge: 'Canopy',
        toolTip: '(Height) Global canopy top height map for the year 2020, visualized in Equal-Earth projection. The underlying data product, estimated from Sentinel-2 imagery, has 10 m ground sampling distance.',
        toolTipURL: 'https://langnico.github.io/globalcanopyheight/',
        type: "Normal",
        region: "Global"
    },
    {
        id: 4,
        name: 'SD (Sentinel-2, Equal-Earth)',
        source: 'eth:eth_g_sd',
        badge: 'Canopy',
        toolTip: '(Standard Deviation) Global canopy top height map for the year 2020, visualized in Equal-Earth projection. The underlying data product, estimated from Sentinel-2 imagery, has 10 m ground sampling distance.',
        toolTipURL: 'https://langnico.github.io/globalcanopyheight/',
        type: "Standard Deviation",
        region: "Global"
    },
    {
        id: 5,
        name: "Digital Surface Model Elevation (National LIDAR Service 1m)",
        source: '9ba4d5ac-d596-445a-9056-dae3ddec0178:Lidar_Composite_Elevation_LZ_DSM_1m',
        badge: 'Elevation',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-surface-model-last-return-dsm-1m/',
        toolTipURL: "https://environment.data.gov.uk/dataset/9ba4d5ac-d596-445a-9056-dae3ddec0178",
        type: "Normal",
        region: "UK"
    },
    {
        id: 7,
        name: "Digital Surface Model Hillshade (National LIDAR Service 1m)",
        source: '9ba4d5ac-d596-445a-9056-dae3ddec0178:Lidar_Composite_Hillshade_LZ_DSM_1m',
        badge: 'Hillshade',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-surface-model-last-return-dsm-1m/',
        toolTipURL: "https://environment.data.gov.uk/dataset/9ba4d5ac-d596-445a-9056-dae3ddec0178",
        type: "Normal",
        region: "UK"
    },
    {
        id: 6,
        name: "Digital Terrain Model Elevation (National LIDAR Service 1m)",
        source: '13787b9a-26a4-4775-8523-806d13af58fc:Lidar_Composite_Elevation_DTM_1m',
        badge: 'Elevation',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-terrain-model-dtm-1m/',
        toolTipURL: "https://environment.data.gov.uk/dataset/13787b9a-26a4-4775-8523-806d13af58fc",
        type: "Normal",
        region: "UK"
    },
    {
        id: 8,
        name: "Digital Terrain Model Hillshade (National LIDAR Service 1m)",
        source: '13787b9a-26a4-4775-8523-806d13af58fc:Lidar_Composite_Hillshade_DTM_1m',
        badge: 'Hillshade',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-terrain-model-dtm-1m/',
        toolTipURL: "https://environment.data.gov.uk/dataset/13787b9a-26a4-4775-8523-806d13af58fc",
        type: "Normal",
        region: "UK"
    },
    {
        id: 9,
        name: "Digital Surface Model Elevation (National LIDAR Service 2m)",
        source: 'f083c5dc-504f-4428-9811-a1b2519fa279:Lidar_Composite_Elevation_LZ_DSM_2m',
        badge: 'Elevation',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-surface-model-last-return-dsm-2m/',
        toolTipURL: "https://environment.data.gov.uk/dataset/f083c5dc-504f-4428-9811-a1b2519fa279",
        type: "Normal",
        region: "UK"
    },
    {
        id: 10,
        name: "Digital Surface Model Hillshade (National LIDAR Service 2m)",
        source: 'f083c5dc-504f-4428-9811-a1b2519fa279:Lidar_Composite_Hillshade_LZ_DSM_2m',
        badge: 'Hillshade',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-surface-model-last-return-dsm-2m/',
        type: "Normal",
        region: "UK"
    },
    {
        id: 11,
        name: "Digital Terrain Model Elevation (National LIDAR Service 2m)",
        source: '09ea3b37-df3a-4e8b-ac69-fb0842227b04:Lidar_Composite_Elevation_DTM_2m',
        badge: 'Elevation',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-terrain-model-dtm-2m/',
        toolTipURL: "https://environment.data.gov.uk/dataset/09ea3b37-df3a-4e8b-ac69-fb0842227b04",
        type: "Normal",
        region: "UK"
    },
    {
        id: 12,
        name: "Digital Terrain Model Hillshade (National LIDAR Service 2m)",
        source: '09ea3b37-df3a-4e8b-ac69-fb0842227b04:Lidar_Composite_Hillshade_DTM_2m',
        badge: 'Hillshade',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-terrain-model-dtm-2m/',
        toolTipURL: "https://environment.data.gov.uk/dataset/09ea3b37-df3a-4e8b-ac69-fb0842227b04",
        type: "Normal",
        region: "UK"
    },
    {
        id: 13,
        name: "Digital Surface Model First Return Elevation (National LIDAR Service 2m)",
        source: '54167602-36c8-4b2b-80ec-ebb6267b6b1e:Lidar_Composite_Elevation_FZ_DSM_2m',
        badge: 'Elevation',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-surface-model-first-return-dsm-2m/',
        toolTipURL: "https://environment.data.gov.uk/dataset/54167602-36c8-4b2b-80ec-ebb6267b6b1e",
        type: "Normal",
        region: "UK"
    },
    {
        id: 14,
        name: "Digital Surface Model First Return Hillshade (National LIDAR Service 2m)",
        source: '54167602-36c8-4b2b-80ec-ebb6267b6b1e:Lidar_Composite_Hillshade_FZ_DSM_2m',
        badge: 'Hillshade',
        externalLink: 'https://environment.data.gov.uk/spatialdata/lidar-composite-digital-surface-model-first-return-dsm-2m/',
        toolTipURL: "https://environment.data.gov.uk/dataset/54167602-36c8-4b2b-80ec-ebb6267b6b1e",
        type: "Normal",
        region: "UK"
    },
    {
        id: 15,
        name: 'LIDAR Vegetation Object Model Elevation',
        source: 'ecae3bef-1e1d-4051-887b-9dc613c928ec:Vegetation_Object_Model_Elevation_2022',
        badge: 'Canopy',
        externalLink: 'https://environment.data.gov.uk/spatialdata/vegetation-object-model/',
        toolTipURL: 'https://www.data.gov.uk/dataset/227ab487-e8f2-4cbb-b26a-9e6d3b662265/lidar-vegetation-object-model-vom#licence-info',
        type: "Normal",
        region: "UK"
    },
    {
        id: 16,
        name: 'ASTER GDEM',
        source: 'ind:dem',
        badge: 'Elevation',
        type: "Normal",
        region: "Indonesia"
    },
    // {
    //     id: 17,
    //     name: 'ASTER GDEM (num)',
    //     source: 'ind:num',
    //     badge: 'Elevation',
    //     type: "Normal",
    //     region: "Indonesia"
    // },
    {
        id: 18,
        name: 'DEMNAS Gorontalo',
        source: 'ind:demnas_gorontalo',
        badge: 'Elevation',
        type: "Normal",
        region: "Indonesia"
    },
    {
        id: 19,
        name: 'DEMNAS Gorontalo Slope',
        source: 'ind:demnas_gorontalo_slope',
        badge: 'Slope',
        type: "Normal",
        region: "Indonesia",
        unit: "degrees"
    }
    
]

export class DigitalModelComponent extends BaseComponent {
    outputCache: Map<string, NumericTileGrid>
    projectZoom: number
    projectExtent: Extent
    maskMode: boolean
    maskLayer: string
    maskCQL: string
    maskShape: string | undefined
    permissions: ProjectPermissions
    modelList: Array<DigitalModel>
    projectProps: ProjectProperties
    constructor(projectProps: ProjectProperties, permissions: ProjectPermissions) {
        super("Digital Model")
        this.category = "Inputs"
        this.outputCache = new Map()
        this.projectExtent = projectProps.extent
        this.projectZoom = projectProps.zoom
        this.maskMode = projectProps.mask
        this.maskLayer = projectProps.maskLayer
        this.maskCQL = projectProps.maskCQL
        this.maskShape = projectProps.maskShape
        this.permissions = permissions
        this.modelList = ModelList.filter(
            a => (this.permissions[a.permission as keyof ProjectPermissions] || !a.permission) && 
            (
                (a.region == "UK" && this.permissions.ukEnabled) || 
                (a.region == "Global") ||
                (a.region == "Indonesia" && this.permissions.indonesiaEnabled)
            )
        )
        this.projectProps = projectProps
    }

    async builder(node: Node) {

        const dft = 'Digital models are generated via LIDAR or satellite data. They can be used to calculate canopy height, feature height, and more.'

        const selectedModel = () => ModelList.find(a => a.id == node.data.sourceId)
        const outputLabel = () => `Output [[${selectedModel()?.unit ?? 'm'}]]`

        node.meta.toolTip = selectedModel()?.toolTip || dft
        node.meta.toolTipLink = selectedModel()?.toolTipURL || ''

        node.addControl(
            new SelectControl(
                this.editor,
                'sourceId',
                () => this.modelList,
                () => {
                    node.meta.toolTip = selectedModel()?.toolTip || dft
                    node.meta.toolTipLink = selectedModel()?.toolTipURL || ''
                    const output = node.outputs.get('dm')
                    if (output) {
                        output.name = outputLabel()
                        node.update()
                    }
                },
                'Model'
            )
        )

        node.addControl(new PreviewControl(() =>
            node.meta.output as any || new NumericTileGrid(0, 0, 0, 1, 1)
        ))

        node.addOutput(new Output('dm', outputLabel(), numericDataSocket))


    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {

        const editorNode = this.editor?.nodes.find(n => n.id === node.id)
        if (editorNode === undefined) { return }

        let index = node.data.sourceId
        if (index === undefined) { index = 0 }

        const extent = this.projectExtent.slice() as Extent
        const zoom = this.projectZoom
        const props = {
            ...this.projectProps,
            extent,
            zoom,
            mask: this.maskMode,
            maskLayer: this.maskLayer,
            maskCQL: this.maskCQL,
            maskShape: this.maskShape,
        }
        const geography = projectGeographyKey(props)
        const mask = await maskFromExtentAndShape(props)
        if (projectGeographyKey(this.projectProps) !== geography) return

        let digitalModel = this.modelList.find(a => a.id == index) ?? this.modelList[0]

        if (digitalModel?.source) {
            const cacheKey = `${geography}::${digitalModel.source}`

            if (this.outputCache.has(cacheKey)) {
                const out = editorNode.meta.output = outputs['dm'] = this.outputCache.get(cacheKey)
            } else {
                const tileGrid = createXYZ()
                const outputTileRange = tileGrid.getTileRangeForExtentAndZ(extent, zoom)
                const isCodedHeight = digitalModel.source === 'eth:eth_g_h' || digitalModel.source === 'eth:eth_g_sd'
                const geotiff = await retrieveModelDataWCS(
                    extent,
                    digitalModel.source,
                    outputTileRange,
                    digitalModel.externalLink,
                    isCodedHeight ? undefined : "bilinear"
                )

                const targetWidth = outputTileRange.getWidth()
                const targetHeight = outputTileRange.getHeight()
                const { rasters, stride } = await readRastersForTileRange(
                    geotiff,
                    targetWidth,
                    targetHeight,
                    isCodedHeight ? "nearest" : "bilinear"
                )

                const out = editorNode.meta.output = outputs['dm'] = new NumericTileGrid(zoom, outputTileRange.minX, outputTileRange.minY, targetWidth, targetHeight)


                for (let i = 0; i < (rasters[0] as TypedArray).length; i++) {

                    let x = (outputTileRange.minX + i % stride)
                    let y = (outputTileRange.minY + Math.floor(i / stride))

                    const raw = rasters[0][i]
                    const nodata = raw === -32767 || (isCodedHeight && raw === 255)
                    out.set(x, y, mask.get(x, y) === true && !nodata ? raw : NaN)

                }

                out.properties['unit'] = digitalModel.unit ?? 'm'
                out.properties['type'] = digitalModel.type

                if (projectGeographyKey(this.projectProps) !== geography) return
                stampGridGeography(out, geography)
                this.outputCache.set(cacheKey, out)

            }
            const previewControl: any = editorNode.controls.get('Preview')
            previewControl.update()
            editorNode.update()
        }

    }

}